import { useState } from 'react';
import { format } from 'date-fns';
import { RefreshCw, Ticket } from 'lucide-react';
import type { ReferralCodeItem, ReferralCodeStatusFilter } from '../api/adminOps';
import { useAuth } from '../components/AuthProvider';
import { DetailDrawer, QueueTable, StatusChip, type QueueColumn } from '../components/ui';
import {
  useCreateReferralCode,
  useReferralCodes,
  useRotateReferralCode,
  useSetReferralCodeStatus,
} from '../hooks/useAdminOps';
import ReferralCohortReport from './ReferralCohortReport';
import {
  codeStatusChip,
  createBlocker,
  normalizeReferralCode,
  reasonBlocker,
  rotateBlocker,
  rotatedFromCode,
} from './referralCodes.logic';

const STATUS_FILTERS: readonly { value: ReferralCodeStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function chip(active: boolean) {
  const config = codeStatusChip(active);
  return <StatusChip label={config.label} tone={config.tone} />;
}

function CreateCodeModal({ onClose }: { onClose: () => void }) {
  const createCode = useCreateReferralCode();
  const [code, setCode] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const blocker = createBlocker({ code, partnerName, organizationId });

  const submit = async () => {
    const trimmedOrganization = organizationId.trim();
    await createCode.mutateAsync({
      code: normalizeReferralCode(code),
      partner_name: partnerName.trim(),
      ...(trimmedOrganization.length > 0 ? { organization_id: trimmedOrganization } : {}),
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="glass-card w-full max-w-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">New referral code</h2>
        <div>
          <label className="label" htmlFor="referral-code">
            Code
          </label>
          <input
            id="referral-code"
            className="input w-full font-mono"
            value={code}
            onChange={event => setCode(event.target.value)}
            placeholder="PARTNER-2026"
          />
          <p className="text-xs text-gray-text mt-1">
            Saved as <span className="font-mono">{normalizeReferralCode(code) || '—'}</span>. Donors
            type this at signup, so it is public.
          </p>
        </div>
        <div>
          <label className="label" htmlFor="referral-partner">
            Partner name
          </label>
          <input
            id="referral-partner"
            className="input w-full"
            value={partnerName}
            onChange={event => setPartnerName(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="referral-organization">
            Organization id (optional)
          </label>
          <input
            id="referral-organization"
            className="input w-full font-mono"
            value={organizationId}
            onChange={event => setOrganizationId(event.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={blocker !== null || createCode.isPending}
            onClick={submit}
          >
            {blocker ?? 'Create code'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CodeActions({ code, onDone }: { code: ReferralCodeItem; onDone: () => void }) {
  const setStatus = useSetReferralCodeStatus();
  const rotate = useRotateReferralCode();
  const [statusReason, setStatusReason] = useState('');
  const [newCode, setNewCode] = useState('');
  const [rotateReason, setRotateReason] = useState('');
  const statusBlocker = reasonBlocker(statusReason);
  const rotation = rotateBlocker(code, newCode, rotateReason);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="label" htmlFor="referral-status-reason">
          {code.active ? 'Deactivate' : 'Activate'} this code
        </label>
        <input
          id="referral-status-reason"
          className="input w-full"
          value={statusReason}
          onChange={event => setStatusReason(event.target.value)}
          placeholder="Reason, for the audit trail"
        />
        <button
          className={code.active ? 'btn-ghost w-full text-red-400' : 'btn-secondary w-full'}
          disabled={statusBlocker !== null || setStatus.isPending}
          onClick={async () => {
            await setStatus.mutateAsync({
              id: code.id,
              active: !code.active,
              reason: statusReason,
            });
            setStatusReason('');
            onDone();
          }}
        >
          {statusBlocker ?? (code.active ? 'Deactivate code' : 'Activate code')}
        </button>
        <p className="text-xs text-gray-text">
          Deactivating stops new signups from using the code. Signups already attributed to it stay
          attributed.
        </p>
      </div>

      {code.active && (
        <div className="space-y-2 border-t border-gray-border pt-3">
          <label className="label" htmlFor="referral-rotate-code">
            Rotate to a new code
          </label>
          {/* DM-7489: partner codes live in printed links (donate-mate.com/p/CODE). */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-yellow-400 text-sm">
              Rotating stops existing links for this partner from working. Donors who use the old
              link or code are not credited to the partner. To retire a code, deactivate it instead.
            </p>
          </div>
          <input
            id="referral-rotate-code"
            className="input w-full font-mono"
            value={newCode}
            onChange={event => setNewCode(event.target.value)}
            placeholder="New public code"
          />
          <input
            className="input w-full"
            aria-label="Rotation reason"
            value={rotateReason}
            onChange={event => setRotateReason(event.target.value)}
            placeholder="Reason, for the audit trail"
          />
          <button
            className="btn-secondary w-full"
            disabled={rotation !== null || rotate.isPending}
            onClick={async () => {
              await rotate.mutateAsync({
                id: code.id,
                newCode: normalizeReferralCode(newCode),
                reason: rotateReason,
              });
              setNewCode('');
              setRotateReason('');
              onDone();
            }}
          >
            {rotation ?? 'Rotate code'}
          </button>
          <p className="text-xs text-gray-text">
            The new code goes live for the same partner and this one is deactivated. Past signups
            stay with this code.
          </p>
        </div>
      )}
    </div>
  );
}

export default function ReferralCodes() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('admin', 'admin-operator');
  const [statusFilter, setStatusFilter] = useState<ReferralCodeStatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useReferralCodes(statusFilter);
  const codes = list.data?.codes ?? [];
  const selected = codes.find(row => row.id === selectedId) ?? null;

  const columns: QueueColumn<ReferralCodeItem>[] = [
    {
      key: 'code',
      header: 'Code',
      render: row => <span className="font-mono font-medium">{row.code}</span>,
    },
    { key: 'partner', header: 'Partner', render: row => row.partner_name },
    { key: 'status', header: 'Status', render: row => chip(row.active) },
    {
      key: 'signups',
      header: 'Verified signups',
      render: row => (
        <span className="tabular-nums">{row.attributed_signups.toLocaleString()}</span>
      ),
    },
    {
      key: 'rotated',
      header: 'Replaces',
      render: row => {
        if (row.rotated_from_code_id === null) return '—';
        const previous = rotatedFromCode(row, codes);
        return (
          <button
            className="font-mono text-primary underline"
            onClick={event => {
              event.stopPropagation();
              if (previous === null) setStatusFilter('all');
              setSelectedId(row.rotated_from_code_id);
            }}
          >
            {previous ?? 'previous code'}
          </button>
        );
      },
    },
    {
      key: 'created',
      header: 'Created',
      render: row => format(new Date(row.created_at), 'yyyy-MM-dd'),
    },
  ];

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <Ticket className="w-6 h-6" /> Referral codes
          </h1>
          <p className="text-gray-text text-sm mt-1">
            Founding Partner codes donors enter at signup, and the cohorts they bring in.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="btn-secondary"
            aria-label="Refresh codes"
            onClick={() => list.refetch()}
          >
            <RefreshCw className={`w-4 h-4 ${list.isFetching ? 'animate-spin' : ''}`} />
          </button>
          {canWrite && (
            <button className="btn-primary" onClick={() => setCreating(true)}>
              New code
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map(option => (
          <button
            key={option.value}
            onClick={() => setStatusFilter(option.value)}
            className={`px-3 py-1 rounded text-sm ${
              statusFilter === option.value
                ? 'bg-primary text-dark'
                : 'bg-dark-lighter text-gray-text'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {list.isError ? (
        <div className="glass-card p-4 space-y-2">
          <p className="text-sm text-red-400">
            Could not load referral codes: {(list.error as Error).message}
          </p>
          <button className="btn-secondary" onClick={() => list.refetch()}>
            Retry
          </button>
        </div>
      ) : (
        <QueueTable
          columns={columns}
          rows={codes}
          rowKey={row => row.id}
          isLoading={list.isPending}
          onRowClick={row => setSelectedId(row.id)}
          emptyTitle="No referral codes"
          emptyHint={
            canWrite ? 'Create one for each Founding Partner.' : 'None have been created yet.'
          }
        />
      )}

      <ReferralCohortReport />

      {creating && <CreateCodeModal onClose={() => setCreating(false)} />}

      <DetailDrawer
        open={selected !== null}
        title={selected ? selected.code : 'Referral code'}
        onClose={() => setSelectedId(null)}
        footer={
          selected && canWrite ? (
            <CodeActions code={selected} onDone={() => setSelectedId(null)} />
          ) : null
        }
      >
        {selected && (
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-gray-text">Partner</dt>
              <dd>{selected.partner_name}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-text">Status</dt>
              <dd>{chip(selected.active)}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-text">Verified signups</dt>
              <dd className="tabular-nums">{selected.attributed_signups.toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-text">Organization</dt>
              <dd className="font-mono break-all">{selected.organization_id ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-text">Created</dt>
              <dd>{new Date(selected.created_at).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-text">Updated</dt>
              <dd>{new Date(selected.updated_at).toLocaleString()}</dd>
            </div>
          </dl>
        )}
      </DetailDrawer>
    </div>
  );
}
